import type { components } from "@/api-schema";
import {
  PathsV1BrandSearchPostParametersQueryOrderByDirection,
  PathsV1BrandSearchPostParametersQueryOrderByField,
  PathsV1BrandSearchPostParametersQuerySelect,
} from "@/api-schema-enums";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData } from "../_types";
import { FilterComponent } from "../base-filter";
import { buildBrandDatIdFilterOptions } from "./_helpers/build-brand-dat-id-filter-options";

type QueryBody = components["schemas"]["BrandSearchBody"];

export class BrandDatIdFilter extends Filter<QueryBody, unknown> {
  readonly id = "datId";
  readonly labelSingular = "masterdata:DATID";
  readonly labelPlural = "masterdata:DATID";
  readonly placeholder = "masterdata:DATID";
  readonly searchPlaceholder = "masterdata:DATID";
  readonly emptyText = "common:NoOptionsFound";

  constructor({ body = {} }: { body?: QueryBody } = {}) {
    super();
    this.body = body;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <BrandDatIdFilterComponent filter={this} {...props} />;
  }
}

function BrandDatIdFilterComponent({ filter, ...props }: { filter: BrandDatIdFilter } & BaseFilterProps) {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination();
  const {
    data: brands,
    isPending: isLoading,
    error,
  } = useBrandSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        searchField: ["datId"],
        select: [PathsV1BrandSearchPostParametersQuerySelect.Uuid, PathsV1BrandSearchPostParametersQuerySelect.DatId],
        orderBy: [
          {
            field: PathsV1BrandSearchPostParametersQueryOrderByField.DatId,
            direction: PathsV1BrandSearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
      },
      body: { ...filter.body, datId: ["IS_NOT_NULL"] },
    },
  });

  const data: FilterData = {
    options: buildBrandDatIdFilterOptions(brands?.data, props.selectedValues),
    isLoading,
    error,
  };
  const pagination = createPaginationState(brands?.meta.total, brands?.data.length ?? 0, isLoading);
  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
